import { useState } from "react"
import { Accordion, AccordionDetails, AccordionSummary, Stack, TextField, Typography } from "@mui/material"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../../../localization/components/AppLocalizationProvider"
import { LocalizedStrict } from "../../../../../../localization/components/AppLocalized"
import { StandardButton } from "../../../../../../../shared/components/buttons/StandardButton"
import { ImageUploadField } from "../../../../../../../shared/components/Formik/ImageUploadField"
import { StandardDialog } from "../../../../../../../shared/components/dialogs/StandardDialog"
import { Allergen } from "../../../../../admin/products/productsTypes"

import { useWeekMenu } from "../../WeekMenuContext"
import { isFieldLocked, localPhotoFromFile } from "../../weekMenuLib"
import { WeekDishRow, WeekMenuCellValues } from "../../weekMenuTypes"
import { AllergenOptionChips } from "./AllergenOptionChips"
import { LabeledDialogField } from "./LabeledDialogField"

const useStyles = makeStyles()((theme) => ({
    content: {
        padding: "0 1.5rem 1.5rem",
    },
    priceRow: {
        maxWidth: "9rem",
    },
    unit: {
        color: theme.palette.tailwind.gray[500],
    },
    accordion: {
        boxShadow: "none",
        border: `1px solid ${theme.palette.tailwind.gray[200]}`,
        borderRadius: ".75rem",
        "&:before": {
            display: "none",
        },
        "&.Mui-expanded": {
            margin: 0,
        },
    },
    accordionSummary: {
        flexDirection: "row-reverse",
        gap: ".75rem",
    },
}))

type Props = Readonly<{
    open: boolean
    row: WeekDishRow
    dishName: string
    initialValues: WeekMenuCellValues
    onSave: (values: WeekMenuCellValues) => void
    onClose: () => void
}>

/** The whole dish in one form, with the extra details in an accordion. */
export function CellDetailsDialog({ open, row, dishName, initialValues, onSave, onClose }: Props) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()

    const [values, setValues] = useState(initialValues)

    const update = (partial: Partial<WeekMenuCellValues>) => setValues((current) => ({ ...current, ...partial }))

    const toggleAllergen = (allergen: Allergen) =>
        update({
            allergens: values.allergens.some((selected) => selected.id === allergen.id)
                ? values.allergens.filter((selected) => selected.id !== allergen.id)
                : [...values.allergens, allergen],
        })

    const showImage = row.image.isShown && !isFieldLocked(row.image)

    return (
        <LocalizedStrict id="merchant-week-menu-details-dialog" attrs={{ dialogTitle: true }}>
            <StandardDialog open={open} dialogTitle="Dish Details" subtitle={dishName} onClose={onClose}>
                <Stack spacing="1.5rem" className={classes.content}>
                    <LabeledDialogField labelId="merchant-week-menu-details-title-label" label="Title">
                        <TextField
                            value={values.title}
                            fullWidth
                            onChange={(event) => update({ title: event.target.value })}
                        />
                    </LabeledDialogField>

                    {row.description.isShown && !isFieldLocked(row.description) && (
                        <LabeledDialogField labelId="merchant-week-menu-details-description-label" label="Description">
                            <TextField
                                value={values.description}
                                multiline
                                minRows={5}
                                fullWidth
                                onChange={(event) => update({ description: event.target.value })}
                            />
                        </LabeledDialogField>
                    )}

                    {row.price.isShown && !isFieldLocked(row.price) && (
                        <LabeledDialogField labelId="merchant-week-menu-details-price-label" label="Price">
                            <Stack direction="row" alignItems="center" spacing=".5rem">
                                <TextField
                                    value={values.price}
                                    className={classes.priceRow}
                                    onChange={(event) => update({ price: event.target.value.replace(/[^0-9.,]/g, "") })}
                                />
                                <LocalizedStrict id="merchant-week-menu-currency-label">
                                    <Typography variant="body2" className={classes.unit}>
                                        DKK
                                    </Typography>
                                </LocalizedStrict>
                            </Stack>
                        </LabeledDialogField>
                    )}

                    {row.allergens.isShown && !isFieldLocked(row.allergens) && (
                        <LabeledDialogField labelId="merchant-week-menu-details-allergens-label" label="Allergens">
                            <AllergenOptionChips selected={values.allergens} onToggle={toggleAllergen} />
                        </LabeledDialogField>
                    )}

                    {row.marketingTag.isShown && !isFieldLocked(row.marketingTag) && (
                        <LabeledDialogField labelId="merchant-week-menu-details-marketing-label" label="Marketing tag">
                            <TextField
                                value={values.marketingTag}
                                fullWidth
                                onChange={(event) => update({ marketingTag: event.target.value })}
                            />
                        </LabeledDialogField>
                    )}

                    {showImage && (
                        <ImageUploadField
                            label={l10n.getString("merchant-week-menu-details-image-label")}
                            previewImage={values.photo?.url}
                            onImageChange={(file) => update({ photo: file ? localPhotoFromFile(file) : null })}
                        />
                    )}

                    <Accordion disableGutters className={classes.accordion}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
                            <LocalizedStrict id="merchant-week-menu-details-accordion-title">
                                <Typography variant="subtitle2">Nutrition &amp; environment</Typography>
                            </LocalizedStrict>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Stack spacing="1.5rem">
                                <LabeledDialogField
                                    labelId="merchant-week-menu-details-ingredients-label"
                                    label="Ingredients"
                                >
                                    <TextField
                                        value={values.details.ingredients}
                                        multiline
                                        minRows={3}
                                        fullWidth
                                        onChange={(event) =>
                                            update({ details: { ...values.details, ingredients: event.target.value } })
                                        }
                                    />
                                </LabeledDialogField>
                                <LabeledDialogField
                                    labelId="merchant-week-menu-details-nutrition-label"
                                    label="Nutrition"
                                >
                                    <TextField
                                        value={values.details.nutrition}
                                        fullWidth
                                        onChange={(event) =>
                                            update({ details: { ...values.details, nutrition: event.target.value } })
                                        }
                                    />
                                </LabeledDialogField>
                                <LabeledDialogField
                                    labelId="merchant-week-menu-details-co2-label"
                                    label="CO₂ Footprint (kg CO₂ eq)"
                                >
                                    <TextField
                                        value={values.details.co2}
                                        fullWidth
                                        onChange={(event) =>
                                            update({
                                                details: {
                                                    ...values.details,
                                                    co2: event.target.value.replace(/[^0-9.,]/g, ""),
                                                },
                                            })
                                        }
                                    />
                                </LabeledDialogField>
                            </Stack>
                        </AccordionDetails>
                    </Accordion>

                    <Stack direction="row" spacing=".75rem">
                        <LocalizedStrict id="merchant-week-menu-details-dialog-close-button">
                            <StandardButton variant="outlined" color="black" onClick={onClose}>
                                Close
                            </StandardButton>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-week-menu-details-dialog-save-button">
                            <StandardButton onClick={() => onSave(values)}>Save</StandardButton>
                        </LocalizedStrict>
                    </Stack>
                </Stack>
            </StandardDialog>
        </LocalizedStrict>
    )
}
